/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { Phone, CheckCircleOutline, InfoOutlined } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import {
  Callout,
  ConsentRow,
  EditPhoneDialog,
  EditPhoneValues,
  InfoBanner,
  OtpInput,
  StepActions,
  PageHeader,
  SkeletonTextoLegal,
  TypographyLegal,
  TypographyOtpHelp,
} from "@components/ui_v2";
import { ConfirmationDialog } from "@components/ConexionSat/ConfirmationDialog";
import { RootState, useAppDispatch } from "@store/store";
import {
  setBuroNip,
  setBuroNipLastCodeRequest,
  setCheckSatBureau,
  setShareholderUpdatePhone,
} from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useValidatePhoneCodeMutation, useRequestPhoneCodeMutation } from "@api/onboarding";
import { usePatchPersonMutation } from "@api/personasApi";
import { BuroValidatorRequestPF, BuroValidatorRequestPFAE, useBuroValidatorMutation } from "@api/sat_apis_new/satApsi";
import { usePostValidateNextStageMutation } from "@api/applications";
import { useLazyGetColonyByIdQuery } from "@api/catalogs";
import { useLazyGetAddressQuery } from "@api/address";
import { ApiError } from "@interfaces/response";
import { formatPhoneNumber, getConfigName, truncateText } from "@helpers/funciones";
import { ADDRESS_CODES, STATUS_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";
import useTextosLegales from "@hooks/useTextosLegales";
import useTipoPersona from "@hooks/useTipoPersona";

/** Empresas que además consultan al accionista como PFAE (`ALLOWED_COMPANIIES` de `ModalBuroNip`). */
const EMPRESAS_BURO_PFAE_ACCIONISTA = ["sfi"];

const TIME_LIMIT = 120;

/** Las urls del `status_config` pueden venir sin "/" inicial o con mayúsculas. */
const normalizeUrl = (url: string) => `/${url.replace(/^\/+/, "")}`.toLowerCase();

export const ConsultaBuro = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const statusConfig = useSelector((state: RootState) => state.app.status_config);
  // El status de esta pantalla es el que el `status_config` le asigna a su url: CSABA en Flux, CSABE en Mercader PM
  // (buró del representante legal). Si no la encuentra, el de siempre.
  const statusCode =
    statusConfig.find((item) => normalizeUrl(item.url) === normalizeUrl(location.pathname))?.code ??
    STATUS_CATALOG.consulta_buro.code;
  const { nextStatus, previousStatus } = useStatusConfig(statusCode);

  const shareholder = useSelector((state: RootState) => state.register.shareholder);
  // Tipo de la persona que autoriza, para el título ("…del representante legal"); `null` mientras se consulta.
  const tipoPersona = useTipoPersona(shareholder.id, "ConsultaBuro");
  const buroNip = useSelector((state: RootState) => state.register.buroNip);
  const application = useSelector((state: RootState) => state.register.application);
  // Textos legales de la empresa (`buro_persona`); mientras cargan, skeleton en lugar del texto por defecto.
  const { textosLegales, cargandoTextos } = useTextosLegales("ConsultaBuro");

  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [validatePhoneCode] = useValidatePhoneCodeMutation();
  const [requestPhoneCode, { isLoading: isRequestingCode }] = useRequestPhoneCodeMutation();
  const [patchPerson] = usePatchPersonMutation();
  const [buroValidator] = useBuroValidatorMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getColony] = useLazyGetColonyByIdQuery();
  const [getAddress] = useLazyGetAddressQuery();

  const resendEffectRanRef = useRef(false);

  const [otp, setOtp] = useState("");
  const [modalOtp, setModalOtp] = useState("");
  const [secondsRemaining, setSecondsRemaining] = useState(TIME_LIMIT);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [rateLimited, setRateLimited] = useState(false);
  const [justResent, setJustResent] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [openEditPhone, setOpenEditPhone] = useState(false);

  // OtpInput / ConsentRow no exponen binding formik — mismo patrón que ValidacionTelefono.
  const otpConfirmCorrecto = modalOtp.length === 4 && modalOtp === buroNip.code;
  const consentOn = buroNip.termsAccepted;

  useEffect(() => {
    if (secondsRemaining <= 0) return;
    const t = setTimeout(() => setSecondsRemaining((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsRemaining]);

  const handleResendCode = async () => {
    try {
      setIsLoading(true);
      setRateLimited(false);

      if (buroNip.isValid) return;
      if (isRequestingCode) return;

      const lastCodeRequest = buroNip.last_code_request ? dayjs(buroNip.last_code_request) : null;
      const currentTime = dayjs();
      const secondsDiff = lastCodeRequest ? currentTime.diff(lastCodeRequest, "seconds") : null;

      if (secondsDiff && secondsDiff < TIME_LIMIT) return;

      await requestPhoneCode({
        country_code: shareholder.code_phone_number,
        phone_number: shareholder.phone_number,
      }).unwrap();

      dispatch(setBuroNipLastCodeRequest({ last_code_request: currentTime.toISOString() }));
      setSecondsRemaining(TIME_LIMIT);
      setJustResent(true);
      setTimeout(() => setJustResent(false), 4000);
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 429) {
        setRateLimited(true);
        return;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateCode = async () => {
    try {
      if (buroNip.isValid) {
        setErrorMessage("");
        return;
      }

      if (otp.length < 4) return;

      setIsLoading(true);
      setErrorMessage("");

      await validatePhoneCode({
        country_code: shareholder.code_phone_number,
        phone_number: shareholder.phone_number,
        code: otp,
      }).unwrap();

      dispatch(
        setBuroNip({
          code: otp,
          isValid: true,
          termsAccepted: false,
        }),
      );
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 404 || apiError?.status === 400) {
        setErrorMessage("Código incorrecto, intenta de nuevo");
        setOtp("");
        return;
      }

      dispatch(setModalErrorVisible({ open: true, error: apiError }));
      Sentry.captureException(apiError, {
        extra: {
          component: "ConsultaBuro",
          values: { otp },
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    validateCode();
  }, [otp]);

  useEffect(() => {
    if (!buroNip.isValid && !resendEffectRanRef.current) {
      resendEffectRanRef.current = true;
      handleResendCode();
    }

    if (buroNip.isValid && buroNip.code) {
      setOtp(buroNip.code);
    }
  }, [buroNip.isValid]);

  useEffect(() => {
    if (buroNip.isValid) return;
    resendEffectRanRef.current = false;
  }, [shareholder.code_phone_number, shareholder.phone_number, buroNip.isValid]);

  const handleConsentClick = () => {
    if (consentOn || !buroNip.isValid) return;
    setModalOtp("");
    setShowConfirmModal(true);
  };

  const handleAcceptConsent = () => {
    if (!otpConfirmCorrecto) return;

    dispatch(
      setBuroNip({
        code: buroNip.code,
        isValid: true,
        termsAccepted: true,
      }),
    );
    setShowConfirmModal(false);
    setModalOtp("");
  };

  const continueToWaitingRoom = async () => {
    if (!nextStatus) {
      throw new Error("No se encontró el siguiente status");
    }

    await triggerPostNextStage({
      applicationId: application.id,
      statusCode: nextStatus.code,
      username: `TOFU - ${accountUser.email}`,
    }).unwrap();

    dispatch(setCheckSatBureau({ check_sat_bureau: true }));
    navigate(nextStatus?.url || "");
  };

  /**
   * Legacy de SFI (`buroForPfae` de `ModalBuroNip`): antes de la consulta como PF, el accionista también se consulta
   * como PFAE, con los mismos datos sin CURP y con `immediate`. A diferencia del legacy, aquí no avanza el estatus
   * (eso lo hace `handleContinue` una sola vez): un 409 (ya consultado) sigue con la consulta PF y cualquier otro
   * error se lanza para mostrar el modal sin avanzar.
   */
  const consultarBuroPfae = async ({ data }: BuroValidatorRequestPF) => {
    const payload: BuroValidatorRequestPFAE = {
      person_Type: "pfae",
      data: {
        person_id: data.person_id,
        rfc: data.rfc,
        loan_application_id: data.loan_application_id,
        application_code: data.application_code,
        first_name: data.first_name,
        second_name: data.second_name,
        father_last_name: data.father_last_name,
        mother_last_name: data.mother_last_name,
        legal_name: data.legal_name,
        transaction_number: data.transaction_number,
        reenter_NIP: true,
        authorization_label_response: true,
        is_endorsement: true,
        immediate: true,
        address: data.address,
      },
    };
    try {
      await buroValidator(payload).unwrap();
    } catch (error) {
      if ((error as ApiError)?.status === 409) return;
      throw error;
    }
  };

  const handleContinue = async () => {
    if (!buroNip.isValid || !buroNip.termsAccepted) return;

    setIsLoading(true);
    try {
      // Domicilio particular de la persona, consultado por `person_id` (no de `address_data`, donde en PM sin CIEC
      // puede estar el de la empresa).
      const addresses = await getAddress({ personId: String(shareholder.id) }).unwrap();
      const domicilio =
        addresses.find((address) => address.address_type?.code === ADDRESS_CODES.particular.code) ?? addresses[0];
      if (!domicilio) {
        throw new Error("No se encontró el domicilio de la persona");
      }

      // "Otra colonia" se guarda sin `colonia_id` y con el nombre en `other_neighborhood`.
      let colonyName = domicilio.other_neighborhood || "";
      if (domicilio.colonia_id) {
        const responseColony = await getColony({ colonyId: domicilio.colonia_id }).unwrap();
        colonyName = responseColony.name || "";
      }

      // se limita a 26 caracteres por campo, ya que es el máximo permitido por BURO
      const first_name = truncateText(shareholder.name, 26);
      const first_lastname = truncateText(shareholder.first_lastname, 26);
      const second_lastname = truncateText(shareholder.second_lastname, 26);
      const colony_name_trim = truncateText(colonyName, 26);
      const exteriorNumber = truncateText(domicilio.no_ext || "", 20);
      const interiorNumber = truncateText(domicilio.no_int || "", 20);

      const payload_buro: BuroValidatorRequestPF = {
        person_Type: "pf",
        data: {
          rfc: shareholder.rfc + shareholder.rfc_homoclave,
          curp: shareholder.curp,
          transaction_number: String(application.id),
          application_code: application.company_application_code,
          legal_name: first_name,
          first_name: first_name,
          second_name: "",
          father_last_name: first_lastname,
          mother_last_name: second_lastname === "" ? "" : second_lastname,
          reenter_NIP: true,
          authorization_label_response: true,
          loan_application_id: String(application.id),
          person_id: String(shareholder?.id),
          is_endorsement: true,
          address: {
            street: domicilio.street,
            exteriorNumber: exteriorNumber,
            interiorNumber: interiorNumber,
            state: domicilio.state?.name ?? "",
            municipality: domicilio.locality?.name ?? "",
            locality: colony_name_trim,
            postalCode: domicilio.cp,
          },
        },
      };

      if (EMPRESAS_BURO_PFAE_ACCIONISTA.includes(String(getConfigName(window.location.href)))) {
        await consultarBuroPfae(payload_buro);
      }

      await buroValidator(payload_buro).unwrap();
      await continueToWaitingRoom();
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.status === 409) {
        try {
          await continueToWaitingRoom();
        } catch (continueError) {
          Sentry.captureException(continueError, {
            extra: { component: "ConsultaBuro", values: {} },
          });
          dispatch(setModalErrorVisible({ open: true, error: continueError as ApiError }));
        }
        return;
      }

      Sentry.captureException(error, {
        extra: { component: "ConsultaBuro", values: {} },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePhone = async (values: EditPhoneValues) => {
    try {
      setIsLoading(true);
      const payload = {
        code_phone_number: values.code_phone_number,
        phone_number: values.phone_number,
      };
      await patchPerson({
        personId: shareholder.id,
        body: payload,
      }).unwrap();

      await requestPhoneCode({
        country_code: values.code_phone_number,
        phone_number: values.phone_number,
      }).unwrap();

      dispatch(setShareholderUpdatePhone(payload));
      setOpenEditPhone(false);
      setSecondsRemaining(TIME_LIMIT);
      setOtp("");
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 429) {
        setRateLimited(true);
        setOpenEditPhone(false);
        return;
      }
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
      Sentry.captureException(apiError, {
        extra: { component: "ConsultaBuroNip - Editar teléfono" },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const autorizado = buroNip.termsAccepted;

  return (
    <Box>
      {/* `S_ValTelefono accionista` del prototipo. El accionista autoriza como persona física (textos PF). */}
      <PageHeader
        eyebrow="Verificación"
        title={
          tipoPersona === null ? (
            <Skeleton variant="text" animation="wave" width="70%" />
          ) : (
            `Validación para consulta buró del ${tipoPersona || "representante legal o accionista mayoritario"}`
          )
        }
        subtitle="Enviamos un código de 4 dígitos por SMS y WhatsApp."
      />

      <Box sx={{ mb: 2 }}>
        <InfoBanner
          icon={<Phone fontSize="small" />}
          label="Teléfono celular"
          value={
            shareholder.phone_number
              ? `(${shareholder.code_phone_number}) ${formatPhoneNumber(String(shareholder.phone_number))}`
              : "—"
          }
          editLabel="Cambiar"
          onEdit={buroNip.isValid ? undefined : () => setOpenEditPhone(true)}
        />
      </Box>

      {autorizado ? (
        <Paper variant="lendia_paper" sx={{ textAlign: "center", p: "32px 24px" }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              bgcolor: "success.light",
              color: "success.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <CheckCircleOutline sx={{ fontSize: 26 }} />
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark" }}>
            Autorización registrada
          </Typography>
          <Typography sx={{ fontSize: 13, color: "tokens.textTertiary", lineHeight: 1.55, mt: 1, mb: 2.5 }}>
            Hemos confirmado tu autorización para consultar el Buró de Crédito de Personas.
          </Typography>
          <ConsentRow
            checked
            onChange={() => undefined}
            disabled
            label={
              cargandoTextos ? (
                <SkeletonTextoLegal lineas={2} />
              ) : (
                <TypographyLegal
                  type="autorizacionBuro"
                  taxSystemType={TAX_SYSTEM_TYPE.PF.code}
                  texto={textosLegales.buro_persona?.check_buro?.texto}
                />
              )
            }
          />
        </Paper>
      ) : (
        <>
          {/* `.ld-verify-card`: la misma tarjeta del código que la validación de teléfono y el NIP. */}
          <Paper
            variant="lendia_paper"
            sx={{
              textAlign: "center",
              p: "36px 28px 28px",
              boxShadow: "0 1px 2px rgba(14, 31, 58, 0.06), 0 1px 3px rgba(14, 31, 58, 0.04)",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <OtpInput length={4} value={otp} onChange={setOtp} size="lg" disabled={isLoading || buroNip.isValid} />
            </Box>

            {errorMessage && (
              <Box sx={{ mt: 2 }}>
                <Callout tone="warn">{errorMessage}</Callout>
              </Box>
            )}

            {rateLimited && (
              <Box sx={{ mt: 2 }}>
                <Callout tone="warn">
                  Excedió el número de intentos permitidos para ese número, intenta más tarde o contacta a un asesor.
                </Callout>
              </Box>
            )}

            {buroNip.isValid ? (
              <Typography sx={{ mt: 2, fontSize: 13, fontWeight: 500, color: "success.main" }}>
                Código verificado
              </Typography>
            ) : (
              <TypographyOtpHelp
                mt={2}
                justResent={justResent}
                secondsRemaining={secondsRemaining}
                onResend={handleResendCode}
                resendDisabled={isLoading}
                testIdPrefix="consulta-buro"
              />
            )}
          </Paper>

          <Paper variant="lendia_paper" sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
            <ConsentRow
              bare
              checked={false}
              onChange={handleConsentClick}
              disabled={!buroNip.isValid || isLoading || cargandoTextos}
              label={
                cargandoTextos ? (
                  <SkeletonTextoLegal lineas={2} />
                ) : (
                  <TypographyLegal
                    type="autorizacionBuro"
                    taxSystemType={TAX_SYSTEM_TYPE.PF.code}
                    texto={textosLegales.buro_persona?.check_buro?.texto}
                  />
                )
              }
            />
            <Callout tone="info" icon={<InfoOutlined sx={{ fontSize: 16 }} />}>
              <Box component="span" sx={{ fontSize: 12.5, lineHeight: 1.55 }}>
                {cargandoTextos ? (
                  <SkeletonTextoLegal lineas={3} />
                ) : (
                  <TypographyLegal type="avisoEvaluacion" texto={textosLegales.buro_persona?.aviso?.texto} />
                )}
              </Box>
            </Callout>
          </Paper>
        </>
      )}

      <StepActions
        onBack={() => {
          if (previousStatus) navigate(previousStatus.url);
        }}
        onNext={handleContinue}
        nextDisabled={!consentOn || isLoading}
        backTestId="consulta-buro-back-button"
        nextTestId="consulta-buro-continue-button"
      />

      <ConfirmationDialog
        open={showConfirmModal}
        title={textosLegales.buro_persona?.modal_buro?.titulo?.trim() || "Consulta a Buró de Crédito"}
        onClose={() => setShowConfirmModal(false)}
        onAccept={handleAcceptConsent}
        closeLabel="Cancelar"
        acceptLabel="Acepto y autorizo"
        acceptDisabled={!otpConfirmCorrecto}
      >
        <Typography component="div" sx={{ fontSize: 13.5, color: "tokens.textSecondary", lineHeight: 1.65, mb: 2 }}>
          {cargandoTextos ? (
            <SkeletonTextoLegal lineas={8} />
          ) : (
            <TypographyLegal
              type="modalBuro"
              taxSystemType={TAX_SYSTEM_TYPE.PF.code}
              parrafos={textosLegales.buro_persona?.modal_buro?.parrafos}
              secciones={textosLegales.buro_persona?.modal_buro?.secciones}
            />
          )}
        </Typography>
        <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "primary.dark", mb: 1.25 }}>
            Confirma tu identidad reingresando el código OTP
          </Typography>
          <OtpInput length={4} value={modalOtp} onChange={setModalOtp} />
          {modalOtp.length === 4 && !otpConfirmCorrecto && (
            <Typography sx={{ fontSize: 12.5, color: "error.main", mt: 1 }}>
              Código incorrecto. Inténtalo de nuevo.
            </Typography>
          )}
        </Box>
      </ConfirmationDialog>

      <EditPhoneDialog
        open={openEditPhone}
        initialValues={{
          code_phone_number: shareholder.code_phone_number,
          phone_number: shareholder.phone_number,
        }}
        onSubmit={handleSavePhone}
        onClose={() => setOpenEditPhone(false)}
        disabled={buroNip.isValid || isLoading}
      />
    </Box>
  );
};
